import Api from '@/api'
import JobBoardPosting from '@/models/JobBoardPosting'
import {
  buildListQuery,
  rejectWithResponse,
  unwrap,
} from '@/utils/storeHelpers'

// Tradie side of company job postings: browse live jobs, apply, withdraw.
// Separate from the `jobPosting` module, which is the business-admin side.
//
// Actions reject with `err.response` (repo convention).

const FILTER_KEYS = ['trade_id', 'state', 'employment_type', 'q']

export default {
  namespaced: true,

  state: {
    jobs: [],
    jobsMeta: {},
    currentJob: null,
    trades: [],
    filters: {},
    applications: [],
    applicationsMeta: {},
  },

  getters: {
    applications: (state) => state.applications,
    applicationsMeta: (state) => state.applicationsMeta,
    jobs: (state) => state.jobs,
    jobsMeta: (state) => state.jobsMeta,
    currentJob: (state) => state.currentJob,
    trades: (state) => state.trades,
    filters: (state) => state.filters,
  },

  mutations: {
    setJobs(state, jobs) {
      state.jobs = jobs
    },
    setJobsMeta(state, meta) {
      state.jobsMeta = meta
    },
    setCurrentJob(state, job) {
      state.currentJob = job
    },
    setTrades(state, trades) {
      state.trades = trades
    },
    setFilters(state, filters) {
      state.filters = filters || {}
    },
    setApplications(state, applications) {
      state.applications = applications
    },
    setApplicationsMeta(state, meta) {
      state.applicationsMeta = meta
    },
    replaceApplication(state, application) {
      state.applications = state.applications.map((a) =>
        a.id === application.id ? { ...a, ...application } : a,
      )
    },
    // A withdrawn application stays in "My applications", marked withdrawn.
    markApplicationWithdrawn(state, jobPostingId) {
      state.applications = state.applications.map((a) =>
        String(a.job_posting_id) === String(jobPostingId)
          ? { ...a, status: 'withdrawn', display_status: 'withdrawn' }
          : a,
      )
    },
    // After sharing contact details, keep the open job's own application in step.
    setMyApplicationShared(state, applicationId) {
      const job = state.currentJob
      if (
        job &&
        job.my_application &&
        job.my_application.id === applicationId
      ) {
        state.currentJob = {
          ...job,
          my_application: {
            ...job.my_application,
            contact_share_status: 'shared',
          },
        }
      }
    },
    // Keep the list row and the open job in step with an apply / withdraw.
    // Withdrawing marks the job "withdrawn" until they apply again.
    setHasApplied(state, { id, hasApplied }) {
      const flags = {
        has_applied: hasApplied,
        has_withdrawn: !hasApplied,
        application_status: hasApplied ? 'new' : null,
      }
      state.jobs = state.jobs.map((j) =>
        String(j.id) === String(id) ? { ...j, ...flags } : j,
      )
      if (state.currentJob && String(state.currentJob.id) === String(id)) {
        state.currentJob = { ...state.currentJob, ...flags }
      }
    },
  },

  actions: {
    fetchJobs({ commit }, payload = {}) {
      commit('setFilters', payload.filters || {})
      return rejectWithResponse(async () => {
        const res = await buildListQuery(
          JobBoardPosting,
          FILTER_KEYS,
          payload,
        ).get()
        commit('setJobs', res.data || [])
        commit('setJobsMeta', res.meta || {})
        return res
      })
    },

    fetchJob({ commit }, id) {
      commit('setCurrentJob', null)
      return rejectWithResponse(async () => {
        const job = unwrap(await Api.get(`jobs-board/${id}`))
        commit('setCurrentJob', job)
        return job
      })
    },

    fetchTrades({ commit }) {
      return rejectWithResponse(async () => {
        const res = await Api.get('jobs-board/trades')
        commit('setTrades', unwrap(res) || [])
      })
    },

    applyToJob({ commit }, { id, message, includeResume }) {
      return rejectWithResponse(async () => {
        const body = { include_resume: !!includeResume }
        if (message) body.message = message
        const res = await Api.post(`jobs-board/${id}/apply`, body)
        commit('setHasApplied', { id, hasApplied: true })
        return unwrap(res)
      })
    },

    // The tradie's own applications, including closed jobs. The API orders them
    // by status (live stages first); `status` filters on the status as read.
    fetchMyApplications({ commit }, { page = 1, perPage = 20, status } = {}) {
      return rejectWithResponse(async () => {
        const params = { page, per_page: perPage }
        if (status) params['filter[status]'] = status
        const res = await Api.get('my-applications', { params })
        const body = res.data || {}
        commit('setApplications', Array.isArray(body.data) ? body.data : [])
        commit('setApplicationsMeta', body.meta || {})
      })
    },

    // Agree to share phone and email with the company for this application.
    shareContact({ commit }, applicationId) {
      return rejectWithResponse(async () => {
        const res = await Api.post(
          `my-applications/${applicationId}/share-contact`,
        )
        const application = unwrap(res)
        commit('replaceApplication', {
          id: applicationId,
          contact_share_status: 'shared',
        })
        commit('setMyApplicationShared', applicationId)
        return application
      })
    },

    withdrawApplication({ commit }, id) {
      return rejectWithResponse(async () => {
        const res = await Api.delete(`jobs-board/${id}/apply`)
        commit('setHasApplied', { id, hasApplied: false })
        commit('markApplicationWithdrawn', id)
        return unwrap(res)
      })
    },
  },
}
